import { lazy, useEffect } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import bbApi from "./api"
import { FullLayout, SingleForm } from "./common/layout"
import { ToastWrapper } from "./common/ui"
import { RequireAuth } from "./common/validation"
import {
  selectIsLoggedIn,
  selectUserDetails,
  logout,
  getMe,
  selectSessionExpired
} from "./redux/slices/auth"
import { useAppDispatch, useAppSelector } from "./common/hooks"
import SessionExpiredModal from "./common/ui/session-expired-modal"

const HomePage = lazy(() => import("./pages/home"))
const Stats = lazy(() => import("./pages/stats"))
const Brands = lazy(() => import("./pages/brands"))
const BrandDetails = lazy(() => import("./pages/brand-details"))
const BrandManage = lazy(() => import("./pages/brand-manage"))
const Integrations = lazy(() => import("./pages/integrations"))
const ApproveIntegrations = lazy(() => import("./pages/approve-integrations"))
const IntegrationNew = lazy(() => import("./pages/integration-new"))
const IntegrationDetails = lazy(() => import("./pages/integration-details"))
const ApiPage = lazy(() => import("./pages/api"))
const LoanOfficers = lazy(() => import("./pages/loan-officers"))
const LODetails = lazy(() => import("./pages/loan-officer-details"))
const PricingLogs = lazy(() => import("./pages/pricing-logs"))
const Rates = lazy(() => import("./pages/rates"))
const RateEngine = lazy(() => import("./pages/rate-engine"))
const Engagements = lazy(() => import("./pages/engagements"))
const EngagementDetails = lazy(() => import("./pages/engagement-details"))
const DreamRateDetailsPage = lazy(() => import("./pages/dream-rate-details"))
const LeadDetailsPage = lazy(() => import("./pages/lead-details"))
const Conversations = lazy(() => import("./pages/conversations"))
const OrganizationDetails = lazy(() => import("./pages/organization-details"))
const Billing = lazy(() => import("./pages/billing"))
const BillingOrgInvoices = lazy(() => import("./pages/billing-org-invoices"))
const BillingInvoice = lazy(() => import("./pages/billing-invoice"))
const Account = lazy(() => import("./pages/account"))
const RecentActivity = lazy(() => import("./pages/recent-activity"))
const ConfigAdmin = lazy(() => import("./pages/config-admin"))
const LeadWorkflowEditPage = lazy(
  () => import("./pages/config-admin/lead-workflow-edit")
)
const RateTableEditPage = lazy(
  () => import("./pages/config-admin/rate-table-edit")
)
const HelocRateTableEditPage = lazy(
  () => import("./pages/config-admin/heloc-rate-table-edit")
)
const HelocWorkflowEditPage = lazy(
  () => import("./pages/config-admin/heloc-workflow-edit")
)
const RateflowEditPage = lazy(
  () => import("./pages/config-admin/rateflow-edit")
)
const ContactFormEditPage = lazy(
  () => import("./pages/config-admin/contact-form-edit")
)
const SubscriptionEditPage = lazy(
  () => import("./pages/config-admin/subscription-edit")
)
const RateCardsEditPage = lazy(
  () => import("./pages/config-admin/rate-cards-edit")
)
const AuditLogs = lazy(() => import("./pages/audit"))
const PricingEngine = lazy(() => import("./pages/pricing-engine"))
const ChannelLeadsPage = lazy(() => import("./pages/channel-leads"))
const ChannelLeadsSystemPage = lazy(
  () => import("./pages/channel-leads-system")
)
const LeadScoring = lazy(() => import("./pages/lead-scoring"))
const StaffAiPage = lazy(() => import("./pages/staff-ai"))
const LeadTimelinePage = lazy(() => import("./pages/lead-timeline"))
const Enroll = lazy(() => import("./pages/enroll"))
const EnrollmentLookup = lazy(() => import("./pages/enrollment-lookup"))
const LoanPrograms = lazy(() => import("./pages/loan-programs"))
const LoanProgramDetails = lazy(() => import("./pages/loan-program-details"))
const CalculatorLibrary = lazy(() => import("./pages/calculator-library"))
const CalculatorPreview = lazy(() => import("./pages/calculator-preview"))
const NotFound = lazy(() => import("./pages/not-found"))
const PlatformUsage = lazy(() => import("./pages/platform-usage"))
const PaymentManagement = lazy(() => import("./pages/payment-management"))

const MyAccountSettings = lazy(
  () => import("./common/ui/account/my-account-settings")
)
const MyLoProfile = lazy(() => import("./common/ui/account/my-lo-profile"))
const BankBilling = lazy(() => import("./common/ui/account/bank-billing"))
const BankInvoice = lazy(() => import("./common/ui/account/bank-invoice"))
const MyEmailPreferences = lazy(
  () => import("./common/ui/account/my-email-preferences")
)
const AccountSecurity = lazy(
  () => import("./common/ui/account/account-security")
)
const FeatureAddons = lazy(() => import("./common/ui/account/feature-addons"))

const Login = lazy(() => import("./pages/login"))
const Password = lazy(() => import("./pages/password"))
const PasswordReset = lazy(() => import("./pages/password-reset"))

const LoginRoute = () => {
  const env = process.env.REACT_APP_ENV

  if (env === "production") {
    window.location.href = "https://app.bankingbridge.com/auth0/login"
    return null
  }

  return <Login />
}

function App() {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const userDetails = useAppSelector(selectUserDetails)
  const isSessionExpired = useAppSelector(selectSessionExpired)
  useEffect(() => {
    // The calculator preview runs the BankingBridge install snippet, which
    // assumes `window.BB` is the widget SDK. This stub has no `init` and
    // stops that snippet from loading the SDK.
    if (window.location.pathname.startsWith("/calculator-preview")) return
    ;(window as any).BB = {
      bbApi
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && !userDetails) {
      dispatch(getMe())
    }
  }, [isLoggedIn, userDetails, dispatch])

  useEffect(() => {
    const validateSession = async () => {
      try {
        await dispatch(getMe()).unwrap()
      } catch (error) {
        dispatch(logout())
      }
    }

    if (isLoggedIn && process.env.REACT_APP_ENV !== "production") {
      validateSession()
    }
  }, [dispatch, isLoggedIn])

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth>
              <FullLayout />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="stats" element={<Stats />} />
          {/* "usage" is deprecated, we keep it temporally */}
          <Route path="usage" element={<Stats />} />
          <Route path="brands" element={<Brands />} />
          <Route path="brands/:brandId" element={<BrandDetails />} />
          <Route path="brands/:brandId/manage" element={<BrandManage />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="integrations/new" element={<IntegrationNew />} />
          <Route path="api" element={<ApiPage />} />
          <Route path="integrations/:id" element={<IntegrationDetails />} />
          <Route path="loan-officers" element={<LoanOfficers />} />
          <Route path="loan-officers/:id" element={<LODetails />} />
          <Route path="pricing-log" element={<PricingLogs />} />
          <Route path="pricing-log/:quoteId" element={<PricingLogs />} />
          <Route
            path="pricing-logs"
            element={<Navigate to="/pricing-log" replace />}
          />
          <Route path="rates" element={<Rates />} />
          <Route path="rates/:id" element={<Rates />} />
          <Route path="rate-engine" element={<RateEngine />} />
          <Route path="engagements" element={<Engagements />} />
          <Route path="engagements/:id" element={<EngagementDetails />} />
          <Route path="dream-rate/:id" element={<DreamRateDetailsPage />} />
          <Route path="leads/:id" element={<LeadDetailsPage />} />
          <Route
            path="conversations"
            element={<Navigate to="/conversations/sms" replace />}
          />
          <Route path="conversations/sms/:_id?" element={<Conversations />} />
          <Route
            path="conversations/advisor/:sessionId?"
            element={<Conversations />}
          />
          <Route
            path="conversations/website-chat/:sessionId?"
            element={<Conversations />}
          />
          <Route path="organizations/:orgId">
            <Route index element={<OrganizationDetails />} />
            <Route path="brands/:brandId" element={<BrandDetails />} />
          </Route>
          <Route path="billing">
            <Route index element={<Billing />} />
            <Route path=":org_id" element={<BillingOrgInvoices />} />
            <Route path=":org_id/:_id" element={<BillingInvoice />} />
          </Route>
          <Route path="account" element={<Account />}>
            <Route index element={<MyAccountSettings />} />
            <Route path="lo-profile" element={<MyLoProfile />} />
            <Route path="billing" element={<BankBilling />} />
            <Route path="billing/:_id" element={<BankInvoice />} />
            <Route path="email-preferences" element={<MyEmailPreferences />} />
            <Route path="security" element={<AccountSecurity />} />
            <Route path="payment-management" element={<PaymentManagement />} />
            <Route path="feature-addons" element={<FeatureAddons />} />
            <Route path="usage" element={<PlatformUsage />} />
          </Route>
          <Route path="recent-activity" element={<RecentActivity />} />
          <Route path="config-admin">
            <Route index element={<ConfigAdmin />} />
            <Route
              path="lead-workflow/:id"
              element={<LeadWorkflowEditPage />}
            />
            <Route path="rate-table/:id" element={<RateTableEditPage />} />
            <Route
              path="heloc-rate-table/:id"
              element={<HelocRateTableEditPage />}
            />
            <Route
              path="heloc-workflow/:id"
              element={<HelocWorkflowEditPage />}
            />
            <Route path="rateflow/:id" element={<RateflowEditPage />} />
            <Route path="contact-form/:id" element={<ContactFormEditPage />} />
            <Route path="rate-cards/:id" element={<RateCardsEditPage />} />
          </Route>
          <Route
            path="config-admin/subscription/:id"
            element={<SubscriptionEditPage />}
          />
          <Route path="audit" element={<AuditLogs />} />
          <Route
            path="approve-integrations"
            element={<ApproveIntegrations />}
          />
          <Route path="pricing-engine" element={<PricingEngine />} />
          <Route
            path="pricing-engine/:rateflowId"
            element={<PricingEngine />}
          />
          <Route path="channel-leads" element={<ChannelLeadsPage />} />
          <Route
            path="channel-leads/system"
            element={<ChannelLeadsSystemPage />}
          />
          <Route path="channel-leads/:orgId" element={<ChannelLeadsPage />} />
          <Route path="lead-scoring" element={<LeadScoring />} />
          <Route path="staff-ai" element={<StaffAiPage />} />
          <Route path="lead-timeline" element={<LeadTimelinePage />} />
          <Route path="enroll" element={<Enroll />} />
          <Route path="enrollment-lookup" element={<EnrollmentLookup />} />
          <Route
            path="enrollment-lookup/id/:enrollmentId"
            element={<EnrollmentLookup />}
          />
          <Route
            path="new-customer"
            element={<Navigate to="/enroll" replace />}
          />
          <Route path="loan-programs">
            <Route index element={<LoanPrograms />} />
            <Route path=":id" element={<LoanProgramDetails />} />
          </Route>
          <Route path="calculator-library" element={<CalculatorLibrary />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/*
          Chrome-free and outside <FullLayout> on purpose: it is a full-viewport
          frame around the standalone calculator, opened in its own tab from the
          calculator library's live preview.
        */}
        <Route
          path="calculator-preview/:slug"
          element={
            <RequireAuth>
              <CalculatorPreview />
            </RequireAuth>
          }
        />
        {/* v2 (new design) counterpart — same page component, distinguished
            by the `/v2/` path segment (see `CalculatorPreview`). */}
        <Route
          path="calculator-preview/v2/:slug"
          element={
            <RequireAuth>
              <CalculatorPreview />
            </RequireAuth>
          }
        />

        <Route path="user" element={<SingleForm />}>
          <Route path="login" element={<LoginRoute />} />
          <Route path="password" element={<Password />} />
          <Route path="password-reset/:token" element={<PasswordReset />} />
        </Route>
      </Routes>
      <ToastWrapper />
      <SessionExpiredModal isOpen={isSessionExpired} />
    </BrowserRouter>
  )
}

export default App
